import { humanTruckType, isShiftTerminal } from "./index";
import { ConnectProto } from "@racoon/api";
// The waybill is handed to `iframe.srcdoc`, so DB-backed values are escaped.
import { escapeHtml } from "~/shared/lib/escape-html";
import { applyAltynTazalykWaybillOverride } from "./print-waybill.altyn-tazalyk";

export interface ShiftData {
  id: string;
  status: ConnectProto.DriverOnShiftStatus;
  plateNumber: string;
  driverName: string;
  type: string;
  model: string;
  /** Trailer of the truck (stored in the truck's `tech_passport` field) */
  trailer?: string;
  releasedAt: string;
  /** Planned start of the shift (`tw_start_at`), ISO string */
  twStartAt?: string;
  mileageBefore: number;
  acceptedAt?: string;
  completed_at?: string;
}

export interface PrintWaybillOptions {
  /** Either the Connect (`bigint` id) or the legacy (`number` id) organization. */
  organization?: { id?: bigint | number; name?: string } | null;
  organizationSettings?: ConnectProto.OrganizationSettings;
  t: (namespace: any, key: string) => string;
}

/**
 * Raw (unescaped) values of the cells printed onto the pre-printed form, one
 * key per `.text-field` class in `getPrintStyles`.
 */
export interface WaybillFields {
  date: string;
  /** "Режим работы" row */
  hours: string;
  /** "Колонна" row */
  orgName: string;
  car: string;
  driver: string;
  trailer: string;
  /** "Сопровождающие лица" row */
  companions: string;
  /** "Задание водителю → в чье распоряжение", departure row */
  departure: string;
  /** "Задание водителю → в чье распоряжение", return row */
  arrival: string;
  departureHour: string;
  departureMinute: string;
  nextDate: string;
  /** "Работа водителя и автомобиля → возврат в гараж" row */
  returnToGarage: string;
  /** "Движение горючего → марка горючего" cell */
  fuelBrand: string;
}

// The org's departure text states the schedule per truck type ("Мультилифты и
// Мусоровозы начинают работу с 06:00, Бункеровозы с 07:00"), so the two time
// boxes on the form follow the truck this waybill is printed for.
const DEPARTURE_TIME_BY_TRUCK_TYPE: Record<number, string> = {
  [ConnectProto.TruckType.DOUBLE_CONTAINER]: "06:00", // Мультилифт
  [ConnectProto.TruckType.MSW]: "06:00", // Мусоровоз ТБО
  [ConnectProto.TruckType.CONSTRUCTION]: "07:00", // Бункеровоз
};

const FALLBACK_DEPARTURE_TIME = "08:00";

/**
 * Older settings prefixed the departure text with the departure time
 * ("07:00 Вывоз мусора г. Алматы"). Such a prefix is split off so it does not
 * collide with the time boxes; a text that opens with words prints in full.
 */
const splitLeadingTime = (text: string) => {
  const match = text.match(/^(\d{1,2}):(\d{2})\s+(.*)$/);
  if (!match) return null;

  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour > 23 || minute > 59) return null;

  return {
    time: `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`,
    rest: match[3],
  };
};

// Function for formatting date for waybill
const formatDateForPermit = (dateString: string): string => {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
};

const nextDayOf = (dateString: string): string => {
  const date = new Date(dateString);
  date.setDate(date.getDate() + 1);
  return date.toISOString();
};

const parseDate = (value?: string): Date | undefined => {
  if (!value) return undefined;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
};

const pad = (value: number) => String(value).padStart(2, "0");

// Create HTML content for a single waybill
const createShiftPermitHTML = (shift: ShiftData, options: PrintWaybillOptions): string => {
  const { organization, organizationSettings, t } = options;

  const shiftSettings = organizationSettings;
  const rawDepartureText = shiftSettings?.shiftDepartureText || "";
  const leadingTime = splitLeadingTime(rawDepartureText);
  const departureText = leadingTime ? leadingTime.rest : rawDepartureText;

  // The shift's planned start (`tw_start_at`) is the time the driver is
  // dispatched on, so it fills the time boxes. The per-truck-type schedule is
  // only a fallback for shifts the backend sent without a planned window; a
  // legacy time prefix covers the types the text says nothing about
  // (Самосвал, Ассенизатор).
  const plannedStart = parseDate(shift.twStartAt);
  const [departureHour, departureMinute] = plannedStart
    ? [pad(plannedStart.getHours()), pad(plannedStart.getMinutes())]
    : (
        DEPARTURE_TIME_BY_TRUCK_TYPE[Number(shift.type)] ??
        leadingTime?.time ??
        FALLBACK_DEPARTURE_TIME
      ).split(":");

  // A shift that starts after 12:00 runs past midnight (a shift lasts 12
  // hours), so the second date box on the form carries the day it ends on.
  const startsAfterNoon =
    !!plannedStart &&
    (plannedStart.getHours() > 12 ||
      (plannedStart.getHours() === 12 && plannedStart.getMinutes() > 0));
  const nextDate = startsAfterNoon ? formatDateForPermit(nextDayOf(shift.releasedAt)) : "";

  const defaults: WaybillFields = {
    date: formatDateForPermit(shift.releasedAt),
    hours: shiftSettings?.shiftWorkingSchedule || "08:00-17:00",
    orgName: organization?.name || "Организация",
    car: `${humanTruckType(Number(shift.type), t)} ${shift.model} ${shift.plateNumber}`,
    driver: shift.driverName,
    trailer: shift.trailer ? `Прицеп ${shift.trailer}` : "",
    companions: "",
    departure: departureText,
    arrival: shiftSettings?.shiftArrivalText || "Возврат",
    departureHour,
    departureMinute,
    nextDate,
    returnToGarage: "",
    fuelBrand: "",
  };
  // TODO(INTEG-258): drop once the waybill is configurable per organization.
  const fields = applyAltynTazalykWaybillOverride(defaults, organization?.id);

  const cells = (Object.keys(fields) as (keyof WaybillFields)[])
    .map((name) => `<div class="text-field ${name}">${escapeHtml(fields[name])}</div>`)
    .join("\n      ");

  return `
    <div class="layout-wrapper">
      ${cells}
    </div>
  `;
};

// CSS styles for printing
const getPrintStyles = (): string => `
  @page {
    size: A4 landscape;
    margin: 0;
  }
  body {
    margin: 0;
    padding: 0;
  }
  .page {
    width: 29.3cm;
    height: 20.1cm;
    position: relative;
    page-break-after: always;
    background: white;
  }
  .page:last-child {
    page-break-after: auto;
  }
  .layout-wrapper {
    position: absolute;
    top: 0;
    right: 0;
    width: 29.3cm;
    height: 20.1cm;
    background: white;
  }
  .text-field {
    position: absolute;
    font-size: 12pt;
    color: black;
  }
  .date { left: 6.8cm; top: 1.3cm; }
  .hours { top: 2.4cm; left: 7.7cm; }
  .car { left: 7.1cm; top: 3.6cm; }
  .driver { left: 7.1cm; top: 4.2cm; }
  .trailer { left: 7.1cm; top: 4.8cm; }
  .companions { left: 7.1cm; top: 5.4cm; }
  .orgName { left: 6.5cm; top: 3cm; }
  .departure { left: 10.3cm; top: 8cm; }
  .arrival { left: 10.3cm; top: 8.7cm; }
  .departureHour { left: 0.4cm; top: 8.1cm; }
  .departureMinute { left: 1.9cm; top: 8.1cm; }
  .nextDate { left: 10cm; top: 1.3cm; }
  .returnToGarage { left: 0.4cm; top: 8.7cm; }
  .fuelBrand { left: 0.4cm; top: 11.4cm; }
`;

// Main function for printing waybills
export const printWaybills = async (
  shifts: ShiftData[],
  options: PrintWaybillOptions,
): Promise<void> => {
  try {
    if (!shifts.length) return;

    let combinedDocument = "";
    for (const shift of shifts) {
      // Finished and cancelled shifts get no waybill.
      if (isShiftTerminal(shift.status) || shift.completed_at) continue;

      if (!shift.plateNumber) continue;
      if (!shift.driverName) continue;

      const shiftPermit = createShiftPermitHTML(shift, options);
      combinedDocument += `<div class="page">${shiftPermit}</div>`;
    }

    if (!combinedDocument) {
      console.warn("Нет активных смен для печати");
      return;
    }

    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    iframe.id = "print-frame";
    document.body.appendChild(iframe);

    iframe.onload = () => {
      iframe.contentDocument?.close();
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);
    };

    iframe.srcdoc = `
      <!doctype html>
      <html lang="ru">
        <head>
          <meta charset="UTF-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1.0" />
          <title>Печать путевых листов</title>
          <style>${getPrintStyles()}</style>
        </head>
        <body>${combinedDocument}</body>
      </html>
    `;
  } catch (error) {
    console.error("Ошибка при печати путевых листов:", error);
  }
};

// Function for printing a single waybill (for compatibility with truck.tab.tsx)
export const printSingleWaybill = async (
  shift: ShiftData,
  options: PrintWaybillOptions,
): Promise<void> => {
  await printWaybills([shift], options);
};
